<template>
  <div class="plan-page">
    <first-run-intent-strip />
    <div class="plan-page__inner">
      <div class="plan-page__logo"><auth-logo /></div>
      <div class="plan-page__head">
        <h1 class="pop-auth__h1">Select a plan</h1>
        <p class="pop-auth__sub plan-page__sub">7 day refund guarantee. Cancel any time within Account Settings.</p>
        <div class="plan-page__toggle" role="tablist">
          <button type="button" class="plan-page__toggle-btn" :class="{ 'is-on': yearly }" role="tab" :aria-selected="yearly" @click="yearly = true">
            Annual <span class="plan-page__badge">Save up to 17%</span>
          </button>
          <button type="button" class="plan-page__toggle-btn" :class="{ 'is-on': !yearly }" role="tab" :aria-selected="!yearly" @click="yearly = false">
            Monthly
          </button>
        </div>
        <div class="plan-page__toggle-note">Annual billing gives you 2 months free on Unlimited and Teams.</div>
        <div class="plan-page__assistant">
          Got questions? <button type="button" class="pop-auth__link" data-test="plan-ask-assistant" @click="openAssistant">Talk to POP assistant.</button>
        </div>
      </div>

      <div v-if="isInheritedAccount" class="plan-page__skip" data-test="plan-inherited">
        <div>
          <div class="plan-page__skip-title">You are in a sub-account</div>
          <div class="pop-auth__fine">Plans are managed by the account owner. Choose another email to subscribe.</div>
        </div>
        <router-link class="plan-page__skip-btn" to="/sites">Back to dashboard</router-link>
      </div>

      <div v-if="!isInheritedAccount" class="plan-page__grid">
        <plan-card v-for="plan in plans" :key="plan.id" :plan="plan" :yearly="yearly" @choose="choose" />
      </div>

      <div v-if="!isInheritedAccount && isSignupStep" class="plan-page__skip">
        <div>
          <div class="plan-page__skip-title">Skip selecting a plan for now</div>
          <div class="pop-auth__fine">You can pick a plan after creating your account.</div>
        </div>
        <button type="button" class="plan-page__skip-btn" data-test="skip-plan" @click="skip">Continue without a plan</button>
      </div>
      <div v-else-if="!isInheritedAccount && !hasActivePlan" class="plan-page__skip">
        <div>
          <div class="plan-page__skip-title">Skip selecting a plan for now</div>
          <div class="pop-auth__fine">You will still get access to your account with an example project.</div>
        </div>
        <button type="button" class="plan-page__skip-btn" data-test="skip-plan" @click="skip">Go to free account</button>
      </div>
      <div v-else class="plan-page__skip plan-page__skip--back">
        <router-link class="pop-auth__link" to="/sites">Back to dashboard</router-link>
      </div>

      <div class="plan-page__footer pop-auth__fine">
        <div>
          You will be charged automatically every {{ yearly ? 'year' : 'month' }}. For more information, please go to
          <a class="pop-auth__link" href="https://www.pageoptimizer.pro/pricing" target="_blank" rel="noopener">pageoptimizer.pro/pricing</a>
        </div>
        <div>*Subject to our <button type="button" class="pop-auth__link" @click="fairUse = true">Fair Use Policy</button></div>
        <div v-if="isSignupStep" style="margin-top: 10px">
          <router-link class="pop-auth__link" :to="signupRoute" data-test="back-to-signup">Back to sign up</router-link>
        </div>
      </div>
    </div>
    <fair-use-modal v-if="fairUse" @close="fairUse = false" />
  </div>
</template>

<script>
import AuthLogo from '../AuthLogo.vue'
import FairUseModal from '../FairUseModal.vue'
import PlanCard from '../PlanCard.vue'
import FirstRunIntentStrip from '../FirstRunIntentStrip.vue'
import { SIGNUP_PLANS, applyCatalog, selectedPlan, formatPrice } from '../plans'
import { segmentPage, segmentTrack } from '@/utils'

const ACTIVE_STATES = ['active', 'free_period', 'admin', 'exempt', 'lapsed']

export default {
  name: 'PlanPickerPage',
  components: { AuthLogo, FairUseModal, PlanCard, FirstRunIntentStrip },
  data() {
    return {
      yearly: true,
      fairUse: false,
      plans: SIGNUP_PLANS,
    }
  },
  computed: {
    user() {
      return this.$store.state.user || {}
    },
    signup() {
      return this.$store.state.signup
    },
    hasActivePlan() {
      if (this.user.planState) return ACTIVE_STATES.includes(this.user.planState)
      return Boolean(this.user.activeSubscriber)
    },
    // /register/plans: opened from the signup card's "Change plan" before the account exists;
    // a choice goes back to the signup page instead of the checkout.
    isSignupStep() {
      return this.$route.name === 'SignupPlans'
    },
    // Sub-accounts and end users: the owner's plan applies, nothing to pick here (PO, 2026-10-20).
    isInheritedAccount() {
      return this.user.planState === 'inherited'
    },
    signupRoute() {
      const query = { ...this.$route.query }
      delete query.product
      return { path: '/register', query }
    },
  },
  created() {
    if (this.signup.plan && this.signup.plan.interval === 'month') this.yearly = false
  },
  async mounted() {
    // Paid users change plans on the account page; only a "?change=1" link opens the picker for them.
    if (this.hasActivePlan && this.$route.query.change !== '1' && this.user.id) {
      this.$router.replace(`/useraccount/${this.user.id}/purchase`)
      return
    }
    segmentPage('Plans', { entry: this.signup.entry })
    this.viewItemForGtm()
    try {
      const response = await this.$store.dispatch('fetchPlanCatalog')
      if (response && response.data && response.data.status === 'SUCCESS') this.plans = applyCatalog(response.data)
    } catch (e) {
      console.log(e)
    }
  },
  methods: {
    choose(plan) {
      const interval = this.yearly && plan.codes.year ? 'year' : 'month'
      const selected = selectedPlan(plan, interval)
      this.$store.commit('signup/setPlan', selected)
      segmentTrack('plan_selected', { servicePackage: selected.code, interval, price: selected.price, fromSignup: this.isSignupStep })
      this.addToCartForGtm(selected)
      if (this.isSignupStep) {
        this.$store.commit('signup/setCoupon', null) // the signup card re-checks the promo code against the new plan
        this.$router.push(this.signupRoute)
        return
      }
      this.$router.push('/plans/checkout')
    },
    openAssistant() {
      const assistant = this.$root.$refs.popAssistantButton
      if (assistant && assistant.open()) segmentTrack('plan_assistant_opened', { entry: this.signup.entry })
    },
    skip() {
      segmentTrack('plan_skipped', { fromCheckout: false, fromSignup: this.isSignupStep, entry: this.signup.entry })
      if (this.isSignupStep) {
        this.$store.commit('signup/setPlan', null)
        this.$store.commit('signup/setCoupon', null)
        this.$router.push(this.signupRoute)
        return
      }
      this.$store.commit('signup/reset')
      this.$router.push('/sites')
    },
    viewItemForGtm() {
      try {
        const plan = this.signup.plan || selectedPlan(this.plans[1], 'year')
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ ecommerce: null })
        window.dataLayer.push({ event: 'view_item', ecommerce: { value: plan.price, currency: 'USD', items: [{
          item_id: plan.code, item_name: `POP ${plan.title}`, item_category: plan.interval === 'year' ? 'Yearly' : 'Monthly',
          price: plan.price, discount: 0 }] } })
      } catch (e) {
        console.log(e)
      }
    },
    addToCartForGtm(plan) {
      try {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ ecommerce: null })
        window.dataLayer.push({ event: 'add_to_cart', ecommerce: { value: plan.price, currency: 'USD', items: [{
          item_id: plan.code, item_name: `POP ${plan.title}`, item_category: plan.interval === 'year' ? 'Yearly' : 'Monthly',
          price: plan.price, discount: 0 }] } })
      } catch (e) {
        console.log(e)
      }
    },
    formatPrice,
  },
}
</script>

<style scoped lang="scss">
@import '../auth-tokens.scss';

.plan-page {
  min-height: 100vh;
  background: $pop-page-bg;
  padding: 32px 5vw 110px;
  color: $pop-teal;
  font-family: 'Mulish', sans-serif;
  text-align: left;

  &__inner { max-width: 1040px; margin: 0 auto; }
  &__logo { display: flex; }
  &__head { text-align: center; margin: 44px 0 30px; }
  &__sub { margin: 0 auto; max-width: 520px; }
  &__toggle {
    display: inline-flex; background: #fff; border: 1px solid $pop-field-border; border-radius: 999px;
    padding: 4px; margin-top: 18px; gap: 2px;
  }
  &__toggle-btn {
    border: 0; border-radius: 999px; padding: 8px 18px; font: inherit; font-size: 13px; font-weight: 700;
    background: transparent; color: $pop-teal; cursor: pointer;
    &.is-on { background: $pop-teal; color: #fff; }
  }
  &__badge { margin-left: 6px; font-weight: 800; color: $pop-success; }
  &__toggle-btn.is-on &__badge { color: #ffcc00; }
  &__assistant { margin-top: 12px; font-size: 13.5px; color: $pop-teal; }
  &__toggle-note { font-size: 12.5px; color: $pop-ink-muted; margin-top: 8px; }
  &__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 20px; align-items: start; }
  &__skip {
    margin: 28px auto 0; max-width: 640px; padding: 22px 26px;
    background: #f1f8f8; border: 1px solid $pop-field-border; border-radius: 18px;
    display: flex; align-items: center; justify-content: space-between; gap: 20px; flex-wrap: wrap;
    &--back { justify-content: center; }
  }
  &__skip-title { font-weight: 800; font-size: 16px; }
  &__skip-btn {
    background: #fff; color: $pop-teal; border: 1px solid $pop-field-border; font: inherit; font-weight: 700; font-size: 14px;
    padding: 12px 22px; border-radius: 10px; cursor: pointer; flex: none;
    &:hover { border-color: $pop-teal; }
  }
  &__footer { text-align: center; margin-top: 26px; line-height: 1.7; }
}
</style>
